import Swal from 'sweetalert2';
import { EventClickArg } from '@fullcalendar/core';
import { Primary, Secondary, Success } from '../../../../constants';
import { TFunction } from 'i18next';
import { cleanupBodyLocks } from '../../../uiKits/modal/common/CommonModal';
import { UserRole } from '../../../../types/Forms';
import { formatWithOffset } from '../../../../lib/timeUtils';

// Function to handle event click actions for calendar events.
export const EventClick = async (
  eventClick: EventClickArg,
  role: string,
  t: TFunction,
  navigateTo?: (to: string) => void,
) => {
  const isAdmin = role === UserRole.ADMIN;
  const isAssistant = role === UserRole.ASSISTANT;
  const isManagement = isAdmin || isAssistant;
  const props = eventClick.event.extendedProps;

  const meetLinkHtml = props.assistant?.latest?.googleMeetLink
    ? `<a href="${props.assistant.latest.googleMeetLink}" target="_blank" rel="noopener noreferrer">${props.assistant.latest.googleMeetLink}</a>`
    : t('courses.exam.calendar.notSetYet');
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const googleMeetLinkBlock = isManagement
    ? ''
    : `        <div class="label">Google Meet link:</div>
        <div class="value">${meetLinkHtml}</div>`;
  const adminSection =
    isManagement && props.assistant?.attempts?.length
      ? (() => {
          const rows = [...props.assistant.attempts]
            // sort by createdAt desc (newest first)
            .sort(
              (a: { createdAt?: string | Date }, b: { createdAt?: string | Date }) =>
                new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime(),
            )
            .map(
              (att: {
                id: number;
                createdAt?: string | Date;
                callTranscriptionId: string | null;
              }) => {
                const created = att.createdAt
                  ? formatWithOffset(att.createdAt, viewerTz, undefined)
                  : '-';
                const hasTranscription = !!att.callTranscriptionId;
                return `
              <tr>
                <td class="p-2">
                  <a href="/exams/details/${att.id}" data-router-link>
                    ID: ${att.id}
                  </a>
                </td>
                <td class="p-2">${t(`courses.exam.calendar.hasTranscription.${hasTranscription}`)}</td>
                <td class="p-2">${created}</td>

              </tr>
            `;
              },
            )
            .join('');

          return `
          <div class="table-responsive mt-2">
          <h6 class="text-muted mb-2 mt-3">${t('courses.exam.calendar.passedExams')}</h6>
            <table class="table table-sm table-bordered table-striped align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="p-2">ID</th>
                  <th scope="col" class="p-2">${t('courses.exam.calendar.examPassed')}</th>

                  <th scope="col" class="p-2">${t('exams.createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>
          </div>
        `;
        })()
      : '';
  // <-- nothing rendered for non-admins

  Swal.fire({
    title: t('courses.exam.calendar.examPopupTitle'),

    html: `
    <div class="event-popup">
 
      <div class="info-grid">
        <div class="label">${t('courses.exam.calendar.eventCourse')}:</div>
        <div class="value">${props.courseTitle ?? '—'}</div>

         <div class="label">${t('courses.exam.calendar.eventCategory')}:</div>
        <div class="value">${props.category}</div>

        <div class="label">${t('courses.exam.calendar.eventLesson')}:</div>
        <div class="value">${eventClick.event.title}</div>
  
        <div class="label">${t('courses.exam.calendar.eventDate')}:</div>
        <div class="value">${eventClick.event.start ? new Date(eventClick.event.start).toLocaleDateString('uk-UA') : '—'}</div>
  
        <div class="label">${t('courses.exam.calendar.eventTime')}:</div>
        <div class="value">${
          eventClick.event.start
            ? new Date(eventClick.event.start).toLocaleTimeString('uk-UA', {
                hour: '2-digit',
                minute: '2-digit',
              })
            : '—'
        }</div>
        
        <div class="label">${t('courses.exam.calendar.eventDescription')}:</div>
        <div class="value calendar-description-value">${props.description.length > 500 ? props.description.slice(0, 500) + '...' : props.description}</div>
  
        <div class="label f-w-400">Email:</div>
        <div class="value">${props.email}</div>

        <div class="label f-w-400">${t('courses.exam.student')}:</div>
        <div class="value">${props.userName}</div>

        <div class="label">${t('courses.exam.examLanguage')}:</div>
        <div class="value">${props.examLang ? props.examLang : t('courses.exam.examLangNotSelected')}</div>

      ${googleMeetLinkBlock}


      </div>
      ${adminSection}  
    </div>
  `,
    showCancelButton: true,
    showConfirmButton: isAdmin,
    confirmButtonColor: '#d33',
    cancelButtonColor: Primary,
    confirmButtonText: t('courses.exam.calendar.eventRemoveEvent'),
    cancelButtonText: t('courses.exam.calendar.eventCloseEvent'),
    showDenyButton: isManagement,
    denyButtonText: t('exams.createExam'),
    denyButtonColor: Secondary,
    didOpen: () => {
      const el = Swal.getHtmlContainer();
      if (!el) return;

      // Delegate clicks on router links
      el.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const a = target.closest('a[data-router-link]') as HTMLAnchorElement | null;
        if (!a) return;

        // allow new-tab/middle-clicks to behave normally
        const mouseEvent = e as MouseEvent;
        if (
          mouseEvent.metaKey ||
          mouseEvent.ctrlKey ||
          mouseEvent.shiftKey ||
          mouseEvent.button === 1
        ) {
          return;
        }

        e.preventDefault();
        const href = a.getAttribute('href');

        if (href && navigateTo) {
          navigateTo(href);
          Swal.close();
        } else if (href) {
          window.location.assign(href);
        }
      });
    },
    willClose: () => {
      cleanupBodyLocks();
    },
  }).then((result) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // exam date may be string/Date; normalize & validate
    const examDate = props.examDate ? new Date(props.examDate) : null;
    const isValidExamDate = examDate instanceof Date && !isNaN(examDate.getTime());
    const isPast = isValidExamDate ? examDate < startOfToday : true; // treat invalid as past

    if ((isAdmin || isAssistant) && result.isConfirmed) {
      eventClick.event.extendedProps.actionHandler({
        examId: props.examId,
        userId: props.userId,
        examDate: props.examDate,
      });
    } else if (result.isDenied) {
      Swal.close();
      requestAnimationFrame(() => {
        if (isPast) {
          Swal.fire({
            title: t('courses.exam.calendar.pastDateTitle') || 'Past date',
            text:
              t('courses.exam.calendar.pastDateText') ||
              'You can add an exam only for today or a future date.',
            icon: 'warning',
            confirmButtonColor: Primary,
          });
          return;
        } else {
          props.openAddForm(props);
        } // <-- run the callback
      });
    }
    // cancel: do nothing
  });
};
